import React, { useMemo, useCallback, useState, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'
import { useTable, useGlobalFilter, useBlockLayout } from 'react-table'
import { matchSorter } from 'match-sorter'

import CreateBotModal from './CreateBotModal'
import EditBotModal from './EditBotModal'
import DeployBotModal from './DeployBotModal'
import CreateFlowModal from './CreateFlowModal'

import { canModifyDeveloperSettings } from 'Selectors/users'
import { getAllBots, getBotById } from 'Selectors/bots'

import { unsetActiveFlow } from 'Actions/flow'
import { deleteBot } from 'Actions/bots'
import useOutsideClickListener from 'LegacyUtils/hooks/useOutsideClickListener'
import { getIsMultiFlowFeatureSupported } from 'Selectors/account'

import flowLogo from 'Assets/images/empty-flow.svg?url'

const Options = ({ botId, showEditBotModal, showDeployBotModal, showCreateFlowModal }) => {
  const [show, setShow] = useState(false)
  const ref = useRef()
  const canModifySettings = useSelector(canModifyDeveloperSettings)
  const isMultiFlowFeatureSupported = useSelector(getIsMultiFlowFeatureSupported)
  const bot = useSelector((state) => getBotById(state, botId))

  const dispatch = useDispatch()
  const closeHandler = useCallback(() => setShow(false), [setShow])

  useOutsideClickListener(ref, closeHandler, show)

  const onAddFlowClick = useCallback(() => {
    showCreateFlowModal(botId)
  }, [showCreateFlowModal, botId])

  const onDeleteClick = useCallback(
    (e) => {
      e.preventDefault()
      const canDelete = confirm('Are you sure you want to delete this bot? This cannot be undone.')
      if (!canDelete) return
      dispatch(deleteBot({ botId }))
    },
    [dispatch, botId]
  )

  const onEditClick = useCallback(() => {
    showEditBotModal(botId)
  }, [showEditBotModal, botId])

  const defaultClick = useCallback(
    (e) => {
      e.preventDefault()
      setShow(!show)
    },
    [show]
  )

  const onDeployClick = useCallback(() => {
    showDeployBotModal(botId)
  }, [showDeployBotModal, botId])

  const onTestClick = useCallback(() => {
    window.open(`/test/${bot.embed_token}`, '_blank')
  }, [bot.embed_token])

  return (
    <div className='options-dots' onClick={defaultClick}>
      {show && (
        <div className='dropdown-menu' ref={ref}>
          <span className='dropdown-menu-arrow' />
          {isMultiFlowFeatureSupported && (
            <a className='dropdown-item' onClick={onAddFlowClick}>
              Add Flow
            </a>
          )}
          <a className='dropdown-item' onClick={onEditClick}>
            Settings
          </a>
          {canModifySettings && (
            <a className='dropdown-item' onClick={onDeployClick}>
              Deploy
            </a>
          )}
          <a className='dropdown-item' onClick={onTestClick}>
            Test
          </a>
          <a className='dropdown-item' onClick={onDeleteClick}>
            Delete
          </a>
        </div>
      )}
    </div>
  )
}

// Search input
// const ChatBotFilter = ({ globalFilter, setFilter }) => {
//   const [value, setValue] = useState(globalFilter)
//   const onChange = useAsyncDebounce(value => {
//     setFilter(value || undefined)
//   }, 200)

//   const onChangeHandler = useCallback((e) => {
//     setValue(e.target.value)
//     onChange(e.target.value)
//   }, [onChange])

//   return (
//     <fieldset className='search-container'>
//       <img src={searchIcon} className='search-icon' alt='' />
//       <input
//         type='text'
//         className='search-input'
//         placeholder='Search'
//         value={value || ''}
//         onChange={onChangeHandler}
//       />
//     </fieldset>
//   )
// }

const fuzzyTextFilterFn = (rows, id, filterValue) => {
  return matchSorter(rows, filterValue, {
    keys: [
      (row) => {
        return row.values[id]
      }
    ]
  })
}

// Let the table remove the filter if the string is empty
fuzzyTextFilterFn.autoRemove = (val) => !val

const Chatbots = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const [showCreateFlowModal, setShowCreateFlowModal] = useState(false)
  const [showCreateBotModal, setShowCreateBotModal] = useState(false)
  const [showEditBotModal, setShowEditBotModal] = useState(false)
  const [activeBotId, setActiveBotId] = useState(null)
  const [showDeployBotModal, setShowDeployBotModal] = useState(false)

  const bots = useSelector(getAllBots)

  const onAddFlowClick = useCallback((botId) => {
    setActiveBotId(botId)
    setShowCreateFlowModal(true)
  }, [])

  const hideCreateFlowModal = useCallback(() => {
    setShowCreateFlowModal(false)
  }, [])

  const onAddBotClick = useCallback(() => {
    setShowCreateBotModal(true)
  }, [])

  const hideCreateBotModal = useCallback(() => {
    setShowCreateBotModal(false)
  }, [])

  const hideEditBotModal = useCallback(() => {
    setShowEditBotModal(false)
  }, [])

  const onShowEditBotModalClick = useCallback((botId) => {
    setActiveBotId(botId)
    setShowEditBotModal(true)
  }, [])

  const onShowDeployBotModalClick = useCallback((botId) => {
    setActiveBotId(botId)
    setShowDeployBotModal(true)
  }, [])

  const hideDeployBotModal = useCallback(() => {
    setShowDeployBotModal(false)
  }, [])

  const onRowClick = useCallback(
    (botId) => {
      dispatch(unsetActiveFlow())
      navigate(`/bots/${botId}`)
    },
    [navigate, dispatch]
  )

  const columns = useMemo(
    () => [
      {
        Header: 'NAME',
        accessor: 'display_name'
      },
      {
        Header: 'CREATED BY',
        accessor: 'user.name'
      },
      {
        Header: 'LAST UPDATED',
        accessor: (row) => row,
        Cell: ({ cell: { value } }) => {
          const lastUpdated = value.updated_at || value.created_at
          return moment(lastUpdated).fromNow()
        }
      }
    ],
    []
  )

  const filterTypes = React.useMemo(
    () => ({
      fuzzyText: fuzzyTextFilterFn
    }),
    []
  )

  const tableOptions = {
    columns,
    data: bots,
    globalFilter: 'fuzzyText',
    filterTypes
  }

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    tableOptions,
    useGlobalFilter,
    useBlockLayout
  )

  return (
    <React.Fragment>
      <CreateBotModal isOpen={showCreateBotModal} hide={hideCreateBotModal} />
      <CreateFlowModal isOpen={showCreateFlowModal} hide={hideCreateFlowModal} botId={activeBotId} />
      {showEditBotModal && <EditBotModal isOpen={showEditBotModal} hide={hideEditBotModal} botId={activeBotId} />}
      <div className='chatbots'>
        <div className='page-top-bar'>
          <div className='title'>Chatbots</div>
          <div className='right'>
            {/* <ChatBotFilter globalFilter={state.globalFilter} setFilter={setGlobalFilter} /> */}
            {rows.length > 0 && (
              <button className='button-primary' onClick={onAddBotClick}>
                New Chatbot
              </button>
            )}
          </div>
        </div>
        {rows.length > 0 && (
          <div>
            <div className='bots-list list-container'>
              <div {...getTableProps()} className='table'>
                <div className='thead'>
                  {headerGroups.map((headerGroup) => {
                    const { key } = headerGroup.getHeaderGroupProps()
                    return (
                      <div className='tr' key={key} {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map((column) => {
                          const { key } = column.getHeaderProps()
                          return (
                            <div className='th' {...column.getHeaderProps()} key={key}>
                              {column.render('Header')}
                            </div>
                          )
                        })}
                        <div className='th options-column' />
                      </div>
                    )
                  })}
                </div>
                <div className='tbody' {...getTableBodyProps()}>
                  {rows.map((row) => {
                    prepareRow(row)
                    const { key, ...rowProps } = row.getRowProps()
                    const botId = row.original.id

                    return (
                      <div className='tr pointer' {...rowProps} key={key}>
                        {row.cells.map((cell) => {
                          const { key, ...cellProps } = cell.getCellProps()
                          return (
                            <div className='td' {...cellProps} key={key} onClick={() => onRowClick(botId)}>
                              {cell.render('Cell')}
                            </div>
                          )
                        })}
                        <div className='options td'>
                          <Options
                            botId={botId}
                            showEditBotModal={onShowEditBotModalClick}
                            showDeployBotModal={onShowDeployBotModalClick}
                            showCreateFlowModal={onAddFlowClick}
                          />
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
        {!rows.length && (
          <div className='empty-state'>
            <div className='empty-state-content'>
              <img className='mb-3' src={flowLogo} alt='' />
              <h1>Build your first chatbot</h1>
              <p>Create decision trees for your Chatbot using the Flow Builder.</p>
              <button onClick={onAddBotClick} className='button-primary mb-3'>
                Create a new Chatbot
              </button>
              <br />
              {/* <a href=''>Learn more about creating decision trees</a> */}
            </div>
          </div>
        )}
      </div>
      {showDeployBotModal && (
        <DeployBotModal isOpen={showDeployBotModal} hide={hideDeployBotModal} botId={activeBotId} />
      )}
    </React.Fragment>
  )
}

export default Chatbots
